import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { VizChartKindKey } from '../../../Generated/VizChartKind.ts'
import type { VizProvenance } from '../../../Generated/VizProvenance.ts'
import type { ChartUpdate } from '../../../Generated/ChartUpdate.ts'

/**
 * Pure, dependency-light helpers over chat viz directives — shared by the slice, the send epic, and the
 * views (kept here, not in a View module, so the Flux layer can import them without an upward dependency).
 */

/** Chart kinds that render through the workspace cube (everything except the standalone table + Krona). */
export const CUBE_KINDS: ReadonlySet<VizChartKindKey> = new Set<VizChartKindKey>([
  'PIE_CHART',
  'TREEMAP',
  'STACKED_BAR',
  'RANKED_BAR',
  'AREA_CHART',
  'LINE_CHART',
  'MAP',
  'BETA_DIVERSITY',
  'RICHNESS',
  'SHANNON',
  'GINI_SIMPSON',
  'ABSOLUTE_EFFECTIVE',
  'OVERLAP_HEATMAP',
  'UPSET',
  'SHARED_UNIQUE'
])

export const isCubeChartKind = (kind: VizChartKindKey): boolean => CUBE_KINDS.has(kind)

export function prettyKind(kind: string): string {
  return kind
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Stable identity of a directive (ignoring `title`): two directives with the same key are "the same chart". */
export function directiveKey(d: VizDirective): string {
  return [
    d.chartKind,
    d.blastSource ?? '',
    d.taxonLevel ?? '',
    d.scalingMode ?? '',
    d.countMode ?? '',
    d.gradeThreshold ?? '',
    d.traitGroup ?? '',
    d.noTraitMode ?? '',
    d.spinAlgorithm ?? '',
    d.spinAtTime ?? '',
    // OTU-table-only view knobs. They're part of the chart's identity so a render/updateChart that changes
    // group mode or sort re-keys the panel → the embedded real table REMOUNTS and re-seeds (its state is
    // seeded once from the directive, so an in-place prop change alone wouldn't take effect). Null for cubes.
    d.groupMode ?? '',
    d.tableSort ?? '',
    (d.selectedPath ?? []).join('>'),
    // taxonEntry is a zoom target like selectedPath — part of identity so a fresh chart on a different
    // taxon is a distinct panel, and an updateChart that changes it re-keys the source to re-resolve + zoom.
    d.taxonEntry ?? '',
    // Per-sink tuning is part of a chart's identity: changing it must re-key the workspace source so the cube
    // rebuilds with the new params (the chat drives the slice additively, keyed on this).
    d.sinkParams ? JSON.stringify(d.sinkParams) : '',
    // Sample grouping/selection (Slice 5) is part of identity too: re-grouping or changing the sample slice
    // must re-key the source so the cube pipeline re-buckets.
    d.groupByField ?? '',
    (d.sampleIds ?? []).join(',')
  ].join('|')
}

function hash(s: string): string {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

/** Short, stable handle for a chart (shown to the model in context; echoed back via focusChart). */
export function graphHandle(d: VizDirective): string {
  return hash(directiveKey(d))
}

/** Deterministic, DOM-safe workspace source id for a cube directive. */
export function cubeSourceId(d: VizDirective): string {
  return `chat-cube-${graphHandle(d)}`
}

/** Human label for a chart, e.g. "Pie Chart (NCBI, family)". */
export function graphLabel(d: VizDirective): string {
  const base = d.title ?? prettyKind(d.chartKind)
  // Don't repeat a param the title already names (the model often puts the taxon in its own title,
  // which otherwise yields e.g. "Treemap (Diptera) (Diptera)").
  const baseLc = base.toLowerCase()
  const params = [d.blastSource, d.taxonEntry]
    .filter(Boolean)
    .filter((p) => !baseLc.includes(String(p).toLowerCase()))
    .join(', ')
  return params ? `${base} (${params})` : base
}

/**
 * Compact, human-readable summary of a chart's RESOLVED viz provenance (the taxonomy lens it actually
 * rendered under, post empty-source substitution) — for the at-a-glance chip on each chart and the canvas-level
 * "current source" indicator. Emits only the fields that are present (all optional), joined by " · ":
 *   - blast source (e.g. `NCBI`, `Consensus`)
 *   - the SPIN algorithm (spin scopes) OR — falling back — the classic group mode (never both)
 *   - the SPIN as-of time, but only when set and not "latest" (the default currency), prefixed `@`
 * Returns '' when nothing is known, so callers can skip rendering. Does NOT recompute anything — reads
 * `provenance` verbatim. e.g. `NCBI · SPCFY`, `Consensus · PROCESS_ID`, `NCBI · SPCFY · @2024-01-02`.
 */
export function formatProvenance(p: VizProvenance | undefined): string {
  if (!p) return ''
  const parts: string[] = []
  if (p.blastSource) parts.push(p.blastSource)
  // SPIN scopes carry an algorithm; classic scopes carry a group mode. They're mutually exclusive in
  // practice, but if both ever arrive, the SPIN lens is the more specific one — prefer it.
  if (p.spinAlgorithm) parts.push(p.spinAlgorithm)
  else if (p.groupMode) parts.push(p.groupMode)
  if (p.spinAtTime && p.spinAtTime.trim().toLowerCase() !== 'latest') parts.push(`@${p.spinAtTime}`)
  return parts.join(' · ')
}

/** Apply an updateChart patch onto a directive — only the patch's non-null fields override. */
export function applyChartUpdate(d: VizDirective, u: ChartUpdate): VizDirective {
  const merged: VizDirective = {
    ...d,
    blastSource: u.blastSource ?? d.blastSource,
    taxonLevel: u.taxonLevel ?? d.taxonLevel,
    scalingMode: u.scalingMode ?? d.scalingMode,
    countMode: u.countMode ?? d.countMode,
    gradeThreshold: u.gradeThreshold ?? d.gradeThreshold,
    selectedPath: u.selectedPath ?? d.selectedPath,
    taxonEntry: u.taxonEntry ?? d.taxonEntry,
    traitGroup: u.traitGroup ?? d.traitGroup,
    noTraitMode: u.noTraitMode ?? d.noTraitMode,
    sinkParams: u.sinkParams ?? d.sinkParams,
    groupByField: u.groupByField ?? d.groupByField,
    sampleIds: u.sampleIds ?? d.sampleIds,
    spinAlgorithm: u.spinAlgorithm ?? d.spinAlgorithm,
    spinAtTime: u.spinAtTime ?? d.spinAtTime,
    tableSort: u.tableSort ?? d.tableSort,
    title: u.title ?? d.title
  }
  // Re-derive the RESOLVED provenance so the chip TRACKS the update. The title/label read the top-level
  // fields above, so if we left `provenance` as the original render's stamp the chip would keep showing the
  // OLD source after an updateChart changed blastSource/spin (e.g. title "…(ncbi)" but chip still "Consensus").
  // updateChart carries an explicit source (no backend auto-route), so the merged top-level fields ARE the
  // resolved lens. Mirrors RenderChartTool's stamp: groupMode only carries on the OTU table.
  return {
    ...merged,
    provenance: {
      blastSource: merged.blastSource,
      spinAlgorithm: merged.spinAlgorithm,
      spinAtTime: merged.spinAtTime,
      groupMode: merged.chartKind === 'OTU_TABLE' ? merged.groupMode : undefined
    }
  }
}

/**
 * Resolve a model-supplied focusChart reference (a handle or a label, possibly fuzzy) to an open graph's
 * directiveKey. Tries exact handle, then label containment, then a chart-kind keyword match.
 */
export function matchFocusRef(graphs: VizDirective[], ref: string): string | undefined {
  const r = ref.trim().toLowerCase()
  if (!r) return undefined
  // The model usually copies the whole context line ("[handle] label …"), so the ref is often LONGER than
  // either the bare handle or the label. Match a handle that appears bare, bracketed, or anywhere in the ref.
  const byHandle = graphs.find((g) => {
    const h = graphHandle(g).toLowerCase()
    return r === h || r.includes(`[${h}]`) || r.includes(h)
  })
  if (byHandle) return directiveKey(byHandle)
  const byLabel = graphs.find((g) => {
    const label = graphLabel(g).toLowerCase()
    return label.includes(r) || r.includes(label) || r.includes(prettyKind(g.chartKind).toLowerCase())
  })
  return byLabel ? directiveKey(byLabel) : undefined
}
